import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, later, next, schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { PaginationDirection } from 'webapp/enums/enums';
import { capitalize, equal } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonNavigationCounterComponent from 'webapp/components/common/navigation-counter';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DatawallWallsSlideComponent from 'webapp/components/datawall/walls-slide';

import type Board from 'webapp/models-custom/board';
import BoardState from 'webapp/models-custom/board-state';
import type DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import { themeClassNames } from 'webapp/models-custom/theme';

interface Args {
	model: Board[];
	slideIndex: number;
	onBoardPreviewHashChange: (boardId: string, previewHash?: string | null) => void;
	onSlideChange: (slideIndex: number) => void;
	onToggleFullscreen?: () => void;
	loopDelay?: number;
	hasHeader?: boolean;
	hasFooter?: boolean;
	isTemplate?: boolean;
	showBottomSlider?: boolean;
	isBoardLoopTemplate?: boolean;
	isInIframe?: boolean;
	showDesignerLink?: boolean;
	skipActions?: boolean;
	trackingOrigin?: string;
}

export default class DatawallWallsSliderComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked boardStates: BoardState[] = [];
	@tracked currentSlideIndex = 0;

	sliderCtEl?: JQuery<HTMLElement>;
	sliderNavEl?: JQuery<HTMLElement>;
	sliderTimerEl?: JQuery<HTMLElement>;

	private _boardIds: string[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.initBoardStates();
		this.updateViewType();
	}

	get isLoadedFromSnapshooter() {
		return this.navigationService.isLoadedFromSnapshooter;
	}

	get showBottomInfo() {
		const hideBottom = this.args.isTemplate && this.isLoadedFromSnapshooter;
		return !hideBottom;
	}

	get showDesignerLink() {
		return this.args.showDesignerLink && !this.isMobileView;
	}

	get slideCount() {
		return this.args.model.length;
	}

	get showSliderArrows() {
		return this.slideCount > 1;
	}

	get currentTheme() {
		return this.boardStates[this.currentSlideIndex]?.boardCM.bgTheme ?? '';
	}

	get themeClass() {
		return themeClassNames(this.currentTheme);
	}

	get boardMenuOptions() {
		return this.args.model.map((board, index) => ({
			index: index,
			text: board.name,
		}));
	}

	// ------ SLIDER SCRIPT -------

	initBoardStates() {
		this.boardStates = this.args.model.map((board) => {
			const boardState = BoardState.createFromBoard(board);
			boardState.onPreviewHashChange = (previewHash) => {
				this.args.onBoardPreviewHashChange(board.boardIdForPublicUrl, previewHash);
			};

			return boardState;
		});
	}

	initSlider(initialSlide = 0, keepState = false) {
		if (!keepState) {
			this.resetSliderTimerProgress();
		}
		const timeLeft = this.loopDelayMs * this.partLeftOnPause;

		this.currentSlideIndex = initialSlide;

		const that = this;
		let currentSlide: number, // hack for mobile where afterChange event triggers without actually changing slide
			speed = 500,
			autoplay = this.hasAutoloop,
			autoplaySpeed = timeLeft;

		if (this.isLoadedFromSnapshooter) {
			speed = 0;
			autoplay = false;
			autoplaySpeed = 0;
		}

		if (this.args.model.length <= 1) {
			this.args.onSlideChange(this.currentSlideIndex);
		}

		const updateColorThemeFn = function () {
			const theme = $(
				`.slick-current .view-board--${that.isMobileView ? 'mobile' : 'wall'}`,
			).hasClass('bgTheme-dark')
				? 'dark'
				: 'light';
			that.sliderNavEl
				?.removeClass('bgTheme-dark bgTheme-light')
				.addClass('bgTheme-' + theme);
		};

		const sliderCtEl = this.sliderCtEl;
		if (!sliderCtEl) {
			return;
		}

		sliderCtEl
			.on('init.slider', function () {
				next(() => {
					if (keepState && that.isPaused) {
						that.sliderCtEl?.slick('slickPause');
					} else {
						that.startSliderTimerProgress();
					}
				});
				updateColorThemeFn();
			})
			.slick({
				speed,
				autoplay,
				autoplaySpeed,
				initialSlide,
				dots: false,
				arrows: false,
				pauseOnFocus: false,
				pauseOnHover: false,
				useTransform: true,
			})
			.on(
				'beforeChange.slider',
				function (_e: JQuery.TriggeredEvent, _s: unknown, cs: number, ns: number) {
					currentSlide = cs;
					that.popupService.closeOnlyContextPopups();
					that.resetSliderTimerProgress();
					that.currentSlideIndex = ns;
				},
			)
			.on(
				'afterChange.slider',
				function (_event: JQuery.TriggeredEvent, _slick: unknown, cs: number) {
					if (currentSlide !== cs) {
						if (!that.isPaused) {
							that.startSliderTimerProgress();
						}
						$('.view-board-inner').scrollTop(0);
						updateColorThemeFn();
						that.args.onSlideChange(cs);
					}
				},
			);

		$(document)
			// we remove the event in case there was model switch in loop preview
			.off('keyup.wallslidernavigation')
			// remove slick's inbuilt slider pausing when window not visible
			.off('visibilitychange mozvisibilitychange webkitvisibilitychange')
			// bind left and right arrows for navigation
			.on('keyup.wallslidernavigation', function (e) {
				if (!that.sliderCtEl) {
					return;
				}
				const isLeftArrow = e.which === 37,
					isRightArrow = e.which === 39;
				if (isLeftArrow || isRightArrow) {
					that.slideDirectionChange(
						isLeftArrow ? PaginationDirection.PREVIOUS : PaginationDirection.NEXT,
					);
				}
			});
	}

	restartSlider(keepPosition = false) {
		const initialSlide = keepPosition ? this.args.slideIndex : 0;

		if (keepPosition) {
			const sliderTimerProgress = this.sliderTimerEl?.find('span');
			const partDone = sliderTimerProgress
				? sliderTimerProgress.width()! / sliderTimerProgress.parent().width()!
				: 0;
			this.partLeftOnPause = 1 - partDone;
		}

		this._destroySlider();

		scheduleOnce('afterRender', this, this._checkNodeOrder);
		scheduleOnce('afterRender', this, this.initSlider, initialSlide, keepPosition);
	}

	_destroySlider() {
		$(document).off('visibilitychange mozvisibilitychange webkitvisibilitychange');
		$(document).off('keyup.wallslidernavigation');
		if (this._isSliderInitialized()) {
			this.sliderCtEl!.slick('unslick').off(
				'init.slider beforeChange.slider afterChange.slider',
			);
		}
	}

	_isSliderInitialized() {
		return this.sliderCtEl?.hasClass('slick-initialized');
	}

	_checkNodeOrder() {
		// checking slide order
		const slickEl = this.sliderCtEl!,
			correctIdOrder = Array.from({ length: this.args.model.length }, (_e, k) => k),
			nodes = slickEl.find('.slide'),
			nodeOrder: number[] = [];

		nodes.each(function (_k, node) {
			const el = $(node),
				order = el.attr('data-order');
			if (order) {
				nodeOrder.push(+order);
			} else {
				el.remove();
			}
		});

		if (!equal(correctIdOrder, nodeOrder)) {
			// sort dom nodes. find them again bcos some might have gotten deleted
			const children = slickEl.find('.slide').get();

			children.sort(function (a, b) {
				const aOrder = +$(a).attr('data-order')!,
					bOrder = +$(b).attr('data-order')!;
				return aOrder < bOrder ? -1 : aOrder === bOrder ? 0 : 1;
			});

			slickEl.append(children);
		}
	}

	@action
	updateViewTypeDebounce() {
		debounce(this, this.updateViewType, 400);
	}

	@action
	refreshSliderPositionDebounce() {
		debounce(this, this.refreshSliderPosition, 400);
	}

	@action
	refreshSliderPosition() {
		// slick caches slide widths at init / on its own resize handler. If the
		// container resizes mid-transition (F11, fullscreen, portrait/mobile
		// class swap), the cached widths get stale and `.slick-track` ends up
		// wider than the viewport. Nudge slick to remeasure once layout settles.
		if (this._isSliderInitialized()) {
			this.sliderCtEl?.slick('setPosition');
		}
	}

	@action
	setup() {
		$(document).on('visibilitychange.wallslider', this.updateViewTypeDebounce);
		$(window).on('orientationchange.wallslider resize.wallslider', this.updateViewTypeDebounce);
		$(window).on('resize.wallslider', this.refreshSliderPositionDebounce);

		// will be used to check if we need to restart slider (onModelChange action)
		this._boardIds = this.args.model.map((item) => item.boardIdForPublicUrl);

		this.sliderCtEl = $('#slider-ct');
		this.sliderNavEl = $('#slider-nav');
		this.sliderTimerEl = $('#slider-timer');

		const initialSlideNotFirstSlide = this.args.slideIndex > 0;

		this.restartSlider(initialSlideNotFirstSlide);
	}

	@action
	teardown() {
		$(document).off('visibilitychange.wallslider');
		$(window).off('orientationchange.wallslider resize.wallslider');
		this._destroySlider();
	}

	@action
	onModelChange() {
		const newIds = this.args.model.map((item) => item.boardIdForPublicUrl),
			isSameIds = equal(this._boardIds, newIds);

		if (!isSameIds) {
			this._boardIds = newIds;

			next(this, this.initBoardStates);
		}

		next(this, this.restartSlider, isSameIds);
	}

	// ------ WALL VS PORTRAIT VS MOBILE VIEWS ------

	@tracked rotateBoxShown = false;
	@tracked isPortraitView = false;
	@tracked isMobileView = false;

	@action
	updateViewType() {
		// using document.clientWidth/Height instad of window.innerWidth/Height
		// bcos of incorrect values returned in mobile on resize / visibilitychange
		// more info https://app.asana.com/0/0/1202471537793018/f

		const winWidth = document.documentElement.clientWidth,
			winHeight = document.documentElement.clientHeight;

		const ratio = winWidth / winHeight,
			isPortrait = ratio < 1,
			isMobileDevice = this.isMobileDevice();

		const isDatawallPortraitView = isPortrait && ratio < 2 / 3 && winWidth >= 1050; // 1080 x 1920 MUST fit
		if (isDatawallPortraitView !== this.isPortraitView) {
			this.isPortraitView = isDatawallPortraitView;
		}

		const isMobileView =
			!isDatawallPortraitView &&
			isPortrait &&
			(winWidth <= 600 || isMobileDevice || this.navigationService.isMobileApp);
		if (isMobileView !== this.isMobileView) {
			this.isMobileView = isMobileView;
		}

		if (isMobileView) {
			// if we've already been on the mobile view, don't need to show rotate icon
			this.rotateBoxShown = true;
		}

		// if landscape that will turn to mobile screen when rotated, show rotate info layer
		if (!isPortrait && isMobileDevice && !this.rotateBoxShown) {
			scheduleOnce('afterRender', this, this.showRotateBoxOnce);
		}
	}

	@action
	changeMdr(dateRange: DateRange, compare: DsCompare | null) {
		this.boardStates.forEach((boardState) => {
			if (
				!boardState.commonMasterDateRange?.isEqual(dateRange.formula) ||
				(boardState.selectedMasterDateRange &&
					!boardState.selectedMasterDateRange.isEqual(dateRange.formula)) ||
				!boardState.selectedMasterCompare?.isEqual(compare?.formula || null)
			) {
				const newDateRange = dateRange.createCopy();
				const newCompare = compare?.createCopy() || null;
				boardState.updateMasterDateRangeSettings(newDateRange, newCompare);
			}
		});
	}

	showRotateBoxOnce() {
		const rotateCt = $('.rotate-box');
		if (rotateCt.length) {
			later(function () {
				rotateCt.fadeIn(1000);
				later(function () {
					rotateCt.fadeOut(function () {
						rotateCt.remove();
					});
				}, 3000);
			}, 2000);
		}
		this.rotateBoxShown = true;
	}

	isMobileDevice() {
		return !!/(Android|webOS|iPhone|iPod|BlackBerry|Windows Phone|Opera Mini)/i.exec(
			navigator.userAgent,
		);
	}

	// ---------- AUTOLOOP ----------

	@tracked partLeftOnPause = 1; // 0 to 1 percentage that we still need to go through
	@tracked isPaused = false;

	get loopDelayMs() {
		return Math.max(this.args.loopDelay || 0, 0) * 1000;
	}

	get hasAutoloop() {
		const loopDelay = this.args.loopDelay;
		return !this.isLoadedFromSnapshooter && loopDelay && loopDelay > 0 && this.slideCount > 1;
	}

	resetSliderTimerProgress() {
		this.partLeftOnPause = 1;

		const sliderTimerEl = this.sliderTimerEl;
		if (sliderTimerEl) {
			sliderTimerEl.toggle(this.hasAutoloop);

			// remove all progress
			sliderTimerEl.find('span').width(0).removeClass('active').attr('style', '');
		}
	}

	startSliderTimerProgress() {
		this.isPaused = false;

		const sliderCt = this.sliderCtEl,
			sliderTimerEl = this.sliderTimerEl,
			sliderTimerProgress = sliderTimerEl?.find('span'),
			timeLeft = this.loopDelayMs * this.partLeftOnPause;

		if (!sliderCt) {
			return;
		}

		sliderCt.slick('slickSetOption', 'autoplay', this.hasAutoloop);
		if (this.hasAutoloop) {
			sliderCt.slick('slickSetOption', 'autoplaySpeed', timeLeft);
			sliderCt.slick('slickPlay');
		} else {
			sliderCt.slick('slickPause');
		}

		sliderTimerEl?.toggle(this.hasAutoloop);

		if (!sliderTimerProgress) {
			return;
		}

		// start animation progress
		sliderTimerProgress.css({
			transition: 'width ' + timeLeft + 'ms linear',
		});

		if (timeLeft) {
			sliderTimerProgress.addClass('active');
		}
	}

	pauseSliderTimerProgress() {
		this.isPaused = true;

		const sliderTimerProgress = this.sliderTimerEl?.find('span');

		if (!sliderTimerProgress) {
			return;
		}

		const partDone = sliderTimerProgress.width()! / sliderTimerProgress.parent().width()!;

		this.partLeftOnPause = 1 - partDone;
		sliderTimerProgress.removeClass('active').css({
			width: sliderTimerProgress.width() + 'px',
			transition: 'none',
		});
	}

	@action
	pause() {
		const mustPause = !this.isPaused;

		if (mustPause) {
			this.pauseSliderTimerProgress();
		} else {
			this.startSliderTimerProgress();
		}

		this.sliderCtEl?.slick(mustPause ? 'slickPause' : 'slickPlay');
	}

	@action
	onLoopDelayChange() {
		if (this._isSliderInitialized()) {
			this.partLeftOnPause = 1;
			this.startSliderTimerProgress();
		}
	}

	@action
	onSlideIndexChange() {
		if (!this._isSliderInitialized()) {
			return;
		}
		const slideIndex = this.args.slideIndex;
		if (slideIndex !== this.sliderCtEl?.slick('slickCurrentSlide')) {
			schedule('afterRender', () => {
				this.sliderCtEl?.slick('slickGoTo', slideIndex);
			});
		}
	}

	@action
	goPrevious() {
		this.slideDirectionChange(PaginationDirection.PREVIOUS);
	}

	@action
	goNext() {
		this.slideDirectionChange(PaginationDirection.NEXT);
	}

	slideDirectionChange(direction = PaginationDirection.NEXT) {
		if (!this._isSliderInitialized()) {
			return;
		}

		this.sliderCtEl?.slick(`slick${capitalize(direction)}`);
	}
	<template>
		<div
			id="slider"
			class={{joinClasses
				(array
					(if @isTemplate "template")
					(if @hasHeader "hasHeader")
					(if @hasFooter "hasFooter")
					(if this.isMobileView "mobile" "wall")
					(if this.isPortraitView "portrait")
					(if this.isLoadedFromSnapshooter "fromSnapshooter")
				)
			}}
			{{didInsert this.setup}}
			{{didUpdate this.onModelChange @model}}
			{{didUpdate this.onLoopDelayChange @loopDelay}}
			{{didUpdate this.onSlideIndexChange @slideIndex}}
			{{willDestroy this.teardown}}
		>

			<div id="slider-ct">
				{{#each this.boardStates as |boardState idx|}}
					<DatawallWallsSlideComponent
						@index={{idx}}
						@boardState={{boardState}}
						@isActive={{eq @slideIndex idx}}
						@showBottomInfo={{this.showBottomInfo}}
						@isFullscreen={{this.isMobileView}}
						@isInIframe={{@isInIframe}}
						@showDesignerLink={{this.showDesignerLink}}
						@skipActions={{@skipActions}}
						@isPortraitView={{this.isPortraitView}}
						@onBoardPreviewHashChange={{@onBoardPreviewHashChange}}
						@onToggleFullscreen={{@onToggleFullscreen}}
						@trackingOrigin={{@trackingOrigin}}
						@slideCount={{this.slideCount}}
						@changeMdr={{this.changeMdr}}
					/>
				{{/each}}
			</div>

			{{#if @showBottomSlider}}
				<div
					id="slider-nav"
					class={{joinClasses (array (if this.themeClass this.themeClass))}}
				>
					<div id="slider-timer"><span></span></div>
					<div class="slider__controls-wrapper">
						<div class="slider__controls">
							{{#if this.showSliderArrows}}
								<div
									role="button"
									class="slider__button slider__button--prev"
									{{on "click" this.goPrevious}}
								>
									<CommonUiIconComponent @name="ChevronDown" @size="sm" />
								</div>
							{{/if}}
							<CommonNavigationCounterComponent
								@items={{this.boardMenuOptions}}
								@selectedIndex={{this.currentSlideIndex}}
								@onSelect={{@onSlideChange}}
								@theme={{this.currentTheme}}
								@class="slider__position"
							/>
							{{#if this.showSliderArrows}}
								<div
									role="button"
									class="slider__button slider__button--next"
									{{on "click" this.goNext}}
								>
									<CommonUiIconComponent @name="ChevronDown" @size="sm" />
								</div>
							{{/if}}
							{{#if this.hasAutoloop}}
								<div
									role="button"
									class="slider__button pauser"
									{{on "click" this.pause}}
								>
									{{#if this.isPaused}}
										<CommonUiIconComponent @name="Play" @size="sm" />
									{{else}}
										<CommonUiIconComponent @name="Pause" @size="sm" />
									{{/if}}
								</div>
							{{/if}}
						</div>
					</div>
				</div>
			{{/if}}

		</div>
	</template>
}
